import { DateTime } from 'luxon'
import { dateSchema, makeNullishSchema } from '../../util/schema'
import { z } from 'zod'
import { AssignmentGroupingType } from '../../firestore/SectionAssignment'

export const AssignNextbookErrors = z.enum([
  'first_due_date_required',
  'time_to_complete_required',
  'frequency_required',
  'first_due_date_invalid',
  'assignments_required',
  'grouping_options_required',
  'grouping_size_invalid',
  'section_id_required',
  'grading_options_required',
  'grading_options_invalid',
  'peer_review_options_required',
  'peer_review_criteria_invalid',
])

const errors = AssignNextbookErrors.Enum

export const AssignNextbookSteps = z.enum([
  'sketch_schedule',
  'confirm_schedule',
  'configure_grouping',
  'configure_grouping_roles', // sub-step
  'configure_peer_reviews', // sub-step
  'configure_grading',
])

// Extract the type from the main steps to ensure type safety
type AssignNextbookStepsType = z.infer<typeof AssignNextbookSteps>
const subSteps = [
  'configure_grouping_roles',
  'configure_peer_reviews',
] as const satisfies readonly AssignNextbookStepsType[]

export const AssignNextbookSubSteps = z.enum(subSteps)

export const AssignNextbookCompletionDurations = z.enum(
  ['3days', '1week', '2weeks', '3weeks', '1month'],
  {
    required_error: errors.time_to_complete_required,
  }
)

export const AssignNextbookFrequencies = z.enum(
  ['weekly', 'biweekly', 'monthly'],
  {
    required_error: errors.frequency_required,
  }
)

export const groupingOptions = z.object({
  customGroupingSelected: z.boolean(),
  groupingType: z.nativeEnum(AssignmentGroupingType),
  groupingSize: z.number().min(1),
  groupingSizeMinimum: z.number().min(1),
  groupingSizeMaximum: z.number().min(1),
  forbidStudentGroupCreation: z.boolean(),
  forbidStudentGroupScheduling: z.boolean(),
  forbidStudentGroupJoining: z.boolean(),
  forbidStudentGroupLeaving: z.boolean(),
  disableGroupingReason: z.string().optional(),
})

export const scalars = z.object({
  participationScore: z.number().min(0).max(1),
  quizScore: z.number().min(0).max(1),
  totalRubricScore: z.number().min(0).max(1),
  attendanceScore: z.number().min(0).max(1),
})

export const gradingOptions = z.object({
  configureGrading: z.boolean(),
  scalars: scalars.optional(),
})

export const peerReviewCriterion = z.object({
  prQuestion: z.string(),
  prDescription: z.string(),
})

export const peerReviewOptions = z.object({
  peerReviewsEnabled: z.boolean(),
  peerReviewCriteria: z.array(peerReviewCriterion),
  peerReviewExcludesSelfReview: z.boolean(),
  peerReviewsHiddenFromReviewees: z.boolean(),
})

const dateSchemaWithPreParse = z.preprocess(
  (val: unknown) => {
    if (typeof val === 'string') return DateTime.fromISO(val)
    return val
  },
  dateSchema({
    required: errors.first_due_date_required,
  })
)

export const schema = z.object({
  sectionId: z.string(),
  firstDueDate: dateSchemaWithPreParse,
  timeToComplete: AssignNextbookCompletionDurations,
  frequency: AssignNextbookFrequencies,
  groupingOptions: groupingOptions,
  peerReviewOptions: peerReviewOptions.optional(),
  gradingOptions: gradingOptions,
  assignments: z.array(
    z.object({
      slideDeckId: z.string(),
      catalogId: z.string(),
      startTime: dateSchemaWithPreParse,
      endTime: dateSchemaWithPreParse,
      groupSizeOverride: z.number().optional(),
    })
  ),
})

export const schemaForLti = schema.omit({
  sectionId: true,
})

export const fieldsSchema = makeNullishSchema(schema)

export const storageSchema = z.object({
  version: z.literal(1),
  timestamp: z.number(),
  payload: fieldsSchema,
})
